import { apiClient } from "@/api/api-client";
import createBaseService from "@/api/base-service.ts";
import type { ContractInvoice, ContractInvoiceDashboard, ContractInvoiceFilters } from "../../types/contract-invoice";

const contractInvoiceService = {
	...createBaseService<ContractInvoice>("/contract-invoices"),

	listByContract: async (contractId: string, formDateFrom: string, formDateTo: string): Promise<{ data: ContractInvoice[] }> => {
		const params: Record<string, unknown> = { sales_contract_id: contractId };

		// Only include date filters if both are provided and not empty
		if (formDateFrom && formDateTo) {
			params.start_date = formDateFrom;
			params.end_date = formDateTo;
		}

		const response = await apiClient.get("/contract-invoices", { params });
		return response.data;
	},

	list: async (params?: {
		sales_contract_id?: string;
		start_date?: string;
		end_date?: string;
		status?: string;
		collection_status?: string;
		search?: string;
		salesperson_id?: string;
		per_page?: number;
		page?: number;
	}): Promise<{ data: ContractInvoice[]; meta: Record<string, unknown>; total_amount?: number }> => {
		const response = await apiClient.get("/contract-invoices", { params });
		return response.data;
	},

	updateCollectionStatus: async (
		invoiceId: string,
		data: {
			collection_status: "pending" | "collected";
			collection_remarks?: string;
			date_collected?: string;
		},
	): Promise<ContractInvoice> => {
		const response = await apiClient.patch(`/contract-invoices/${invoiceId}/collection-status`, data);
		return response.data;
	},

	markAsPaid: async (invoiceId: string, data: { payment_method?: string }): Promise<ContractInvoice> => {
		const response = await apiClient.patch(`/contract-invoices/${invoiceId}/mark-as-paid`, data);
		return response.data;
	},

	getDashboard: async (dateFrom?: string, dateTo?: string, salespersonId?: string): Promise<ContractInvoiceDashboard> => {
		const response = await apiClient.get("/contract-invoices-dashboard", {
			params: { date_from: dateFrom, date_to: dateTo, salesperson_id: salespersonId },
		});
		return response.data;
	},

	generateMissingInvoices: async (): Promise<{ message: string; total_generated: number; invoices: ContractInvoice[] }> => {
		const response = await apiClient.post("/contract-invoices/generate-missing");
		return response.data;
	},

	generateForContract: async (contractId: string): Promise<{ message: string; total_generated: number; invoices: ContractInvoice[] }> => {
		const response = await apiClient.post(`/contract-invoices/generate-for-contract/${contractId}`);
		return response.data;
	},

	getCollectionReport: async (year: number, salespersonId?: string): Promise<any[]> => {
		const response = await apiClient.get("/contract-invoices-collection-report", {
			params: { year, salesperson_id: salespersonId },
		});
		return response.data;
	},

	getUpcomingDueDates: async (days?: number, salespersonId?: string): Promise<any[]> => {
		const response = await apiClient.get("/contract-invoices-upcoming-due-dates", {
			params: { days, salesperson_id: salespersonId },
		});
		return response.data;
	},

	downloadPdf: (invoiceId: string) => {
		window.open(`/api/contract-invoices/${invoiceId}/download`, "_blank");
	},

	downloadDocx: (invoiceId: string) => {
		window.open(`/api/contract-invoices/${invoiceId}/download-docx`, "_blank");
	},
};

export default contractInvoiceService;
